/**
 * PdfViewerPanel - PDF viewer with OCR bounding box overlays.
 *
 * Renders PDF via react-pdf with optional entity highlights.
 * Includes zoom controls: +/- buttons, percentage label, fit-to-width reset.
 */

import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import { getEntityColor, getBorderColor } from './entityColors';
import { config } from '../../utils/config';

pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.mjs';

export interface EntityWithBounds {
    type: string;
    value: string;
    confidence: number;
    bounding_box?: Array<{ x: number; y: number }> | null;
    page: number;
    source: string;
}

interface PdfViewerPanelProps {
    documentId: string;
    parentDocumentId?: string | null;
    entities: EntityWithBounds[];
    showHighlights: boolean;
    initialPage?: number;
}

interface CanvasRect {
    width: number;
    height: number;
    left: number;
    top: number;
}

const MIN_SCALE = 0.5;
const MAX_SCALE = 3.0;
const SCALE_STEP = 0.25;
const DEFAULT_SCALE = 1.0;
const QUICK_ZOOM_SCALE = 2.0;
const BASE_WIDTH = 560;

const PdfViewerPanel: React.FC<PdfViewerPanelProps> = ({
    documentId, parentDocumentId, entities, showHighlights, initialPage
}) => {
    const [numPages, setNumPages] = useState<number | null>(null);
    const [pageNumber, setPageNumber] = useState(1);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [canvasRect, setCanvasRect] = useState<CanvasRect | null>(null);
    const [scale, setScale] = useState(DEFAULT_SCALE);
    const pageWrapperRef = useRef<HTMLDivElement>(null);

    // Build file URL - use parent document for child docs
    const fileUrl = useMemo(() => {
        const token = localStorage.getItem('auth_token');
        const docId = parentDocumentId || documentId;
        return `${config.API_URL}/api/documents/${docId}/download?token=${token}`;
    }, [documentId, parentDocumentId]);

    // Auto-navigate to initial page (for child documents)
    useEffect(() => {
        if (initialPage != null && initialPage >= 0) {
            setPageNumber(initialPage + 1); // Convert 0-indexed to 1-indexed
        }
    }, [initialPage]);

    const handleLoadSuccess = useCallback(({ numPages: total }: { numPages: number }) => {
        setNumPages(total);
        setLoading(false);
        setError(null);
    }, []);

    const handleLoadError = useCallback((err: Error) => {
        setError('PDF yuklenemedi');
        setLoading(false);
        console.error('PDF load error:', err);
    }, []);

    const updateCanvasRect = useCallback(() => {
        if (!pageWrapperRef.current) return;
        const canvas = pageWrapperRef.current.querySelector('canvas');
        if (!canvas) return;
        const wrapperRect = pageWrapperRef.current.getBoundingClientRect();
        const canvasRectVal = canvas.getBoundingClientRect();
        setCanvasRect({
            width: canvas.offsetWidth,
            height: canvas.offsetHeight,
            left: canvasRectVal.left - wrapperRect.left,
            top: canvasRectVal.top - wrapperRect.top,
        });
    }, []);

    const handleRenderSuccess = useCallback(() => {
        updateCanvasRect();
    }, [updateCanvasRect]);

    // Recalculate on window resize
    useEffect(() => {
        window.addEventListener('resize', updateCanvasRect);
        return () => window.removeEventListener('resize', updateCanvasRect);
    }, [updateCanvasRect]);

    const goToPrevPage = useCallback(() => {
        setPageNumber(prev => Math.max(1, prev - 1));
        setCanvasRect(null);
    }, []);

    const goToNextPage = useCallback(() => {
        setPageNumber(prev => Math.min(numPages || prev, prev + 1));
        setCanvasRect(null);
    }, [numPages]);

    // Zoom handlers
    const zoomIn = useCallback(() => {
        setScale(prev => Math.min(MAX_SCALE, prev + SCALE_STEP));
    }, []);

    const zoomOut = useCallback(() => {
        setScale(prev => Math.max(MIN_SCALE, prev - SCALE_STEP));
    }, []);

    const zoomReset = useCallback(() => {
        setScale(DEFAULT_SCALE);
    }, []);

    const handleContentClick = useCallback(() => {
        setScale(prev => prev === DEFAULT_SCALE ? QUICK_ZOOM_SCALE : DEFAULT_SCALE);
    }, []);

    // Filter entities for current page (0-indexed page field vs 1-indexed pageNumber)
    const pageEntities = useMemo(() => {
        if (!showHighlights) return [];
        return entities.filter(e =>
            e.bounding_box && e.bounding_box.length >= 4 && e.page === pageNumber - 1
        );
    }, [entities, pageNumber, showHighlights]);

    const getBboxStyle = useCallback((entity: EntityWithBounds): React.CSSProperties | null => {
        if (!canvasRect || !entity.bounding_box || entity.bounding_box.length < 4) return null;
        const vertices = entity.bounding_box;
        const xs = vertices.map(v => v.x || 0);
        const ys = vertices.map(v => v.y || 0);
        const minX = Math.min(...xs);
        const minY = Math.min(...ys);
        const maxX = Math.max(...xs);
        const maxY = Math.max(...ys);

        const left = minX * canvasRect.width;
        const top = minY * canvasRect.height;
        const width = (maxX - minX) * canvasRect.width;
        const height = (maxY - minY) * canvasRect.height;

        if (width < 5 || height < 5) return null;

        return {
            position: 'absolute',
            left: `${left}px`,
            top: `${top}px`,
            width: `${width}px`,
            height: `${height}px`,
            backgroundColor: getEntityColor(entity.type),
            border: `1.5px solid ${getBorderColor(entity.type)}`,
            borderRadius: '2px',
            pointerEvents: 'none',
        };
    }, [canvasRect]);

    if (error) {
        return (
            <div className="pdf-viewer-panel">
                <div className="pdf-viewer-panel__error">{error}</div>
            </div>
        );
    }

    return (
        <div className="pdf-viewer-panel">
            <div className="pdf-viewer-panel__zoom-bar">
                <button onClick={zoomOut} disabled={scale <= MIN_SCALE} title="Kucult">
                    &#x2212;
                </button>
                <span className="pdf-viewer-panel__zoom-label">
                    {Math.round(scale * 100)}%
                </span>
                <button onClick={zoomIn} disabled={scale >= MAX_SCALE} title="Buyut">
                    +
                </button>
                <button
                    className="pdf-viewer-panel__zoom-fit"
                    onClick={zoomReset}
                    disabled={scale === DEFAULT_SCALE}
                    title="Sigdir"
                >
                    Sigdir
                </button>
            </div>

            <div
                className="pdf-viewer-panel__content"
                onClick={handleContentClick}
                style={{ cursor: scale === DEFAULT_SCALE ? 'zoom-in' : 'zoom-out' }}
            >
                <div className="pdf-viewer-panel__page-wrapper" ref={pageWrapperRef}>
                    <Document
                        file={fileUrl}
                        onLoadSuccess={handleLoadSuccess}
                        onLoadError={handleLoadError}
                        loading={
                            <div className="pdf-viewer-panel__loading">PDF yukleniyor...</div>
                        }
                    >
                        <Page
                            pageNumber={pageNumber}
                            renderTextLayer={false}
                            renderAnnotationLayer={false}
                            onRenderSuccess={handleRenderSuccess}
                            width={BASE_WIDTH * scale}
                        />
                    </Document>

                    {canvasRect && pageEntities.length > 0 && (
                        <div
                            className="pdf-viewer-panel__overlay"
                            style={{
                                width: canvasRect.width,
                                height: canvasRect.height,
                                left: canvasRect.left,
                                top: canvasRect.top,
                            }}
                        >
                            {pageEntities.map((entity, idx) => {
                                const style = getBboxStyle(entity);
                                if (!style) return null;
                                return (
                                    <div
                                        key={`${entity.type}-${idx}`}
                                        style={style}
                                        title={`${entity.type}: ${entity.value}`}
                                    />
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>

            {numPages && numPages > 1 && (
                <div className="pdf-viewer-panel__controls">
                    <button onClick={goToPrevPage} disabled={pageNumber <= 1}>
                        &#x2039;
                    </button>
                    <span>{pageNumber} / {numPages}</span>
                    <button onClick={goToNextPage} disabled={pageNumber >= numPages}>
                        &#x203a;
                    </button>
                </div>
            )}
        </div>
    );
};

export default PdfViewerPanel;
